/* eslint-disable react/prop-types */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import {
  Sparkles, ChevronDown, ChevronUp, Loader2, X, Plus, Trash2,
  AlertTriangle, Info, FileJson,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

/**
 * Tracking OS V2 — Paste-to-Template Generator panel.
 *
 * Sits ABOVE the existing manual builder. Calls the new generator endpoint,
 * shows a fully-editable preview modal, and saves through the EXISTING
 * /api/tracking/templates POST. Never auto-publishes — status forced to
 * `draft` server-side.
 *
 * Props:
 *   onSaved(savedTemplate)  — invoked after successful save so the parent
 *                             list can refresh.
 */
export default function TrackingTemplateGenerator({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [formTypeHint, setFormTypeHint] = useState('auto');
  const [useLlm, setUseLlm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [jsonPaste, setJsonPaste] = useState('');
  const [generated, setGenerated] = useState(null); // {draft, warnings, parser_used, token_usage, cost_estimate_inr}

  const reset = () => {
    setText(''); setJsonPaste(''); setGenerated(null);
    setFormTypeHint('auto'); setUseLlm(true);
  };

  const generate = async () => {
    if (text.trim().length < 20) {
      toast.error('Paste a longer form spec (minimum 20 chars).');
      return;
    }
    setLoading(true);
    try {
      const body = {
        text: text.trim(),
        use_llm: useLlm,
        form_type_hint: formTypeHint === 'auto' ? null : formTypeHint,
      };
      const r = await axios.post(`${API}/tracking/templates/generate-from-text`, body);
      setGenerated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const generateFromJson = async () => {
    let parsed;
    try {
      parsed = JSON.parse(jsonPaste);
    } catch {
      toast.error('Invalid JSON');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/tracking/templates/generate-from-json`, {
        template: parsed,
      });
      setGenerated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="tracking-os-generate-panel"
      className="mb-4 rounded-lg border border-cyan-500/20 bg-cyan-500/5"
    >
      <button
        type="button"
        data-testid="tracking-os-generate-panel-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-cyan-500/10 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-cyan-100">
          <Sparkles className="w-4 h-4" />
          Generate Template from Text
          <span className="text-[11px] text-cyan-400/70 ml-2 font-normal">
            (paste a form spec — admin reviews before save)
          </span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-cyan-300" /> : <ChevronDown className="w-4 h-4 text-cyan-300" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <Textarea
            data-testid="tracking-os-generate-textarea"
            placeholder="Paste your form spec here.&#10;&#10;Example:&#10;Title: Execution Insight Form&#10;Form type: one_time&#10;Subscriber title: Execution Check-in&#10;&#10;Section 1 — Current Experience&#10;Q1. How consistent were you this week?&#10;Single choice:&#10;- Fully consistent&#10;- Mostly&#10;- Sometimes&#10;- Not at all"
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="font-mono text-xs bg-slate-950/50 border-white/10"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] text-white/60 mb-1 block">Form-type hint</Label>
              <Select value={formTypeHint} onValueChange={setFormTypeHint}>
                <SelectTrigger data-testid="tracking-os-generate-form-type" className="bg-white/5 border-white/10 h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect</SelectItem>
                  <SelectItem value="one_time">one_time</SelectItem>
                  <SelectItem value="event">event</SelectItem>
                  <SelectItem value="daily">daily</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-white/60 mb-1 block">Mode</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="tracking-os-generate-mode-llm"
                  onClick={() => setUseLlm(true)}
                  className={`flex-1 h-9 text-xs rounded border transition-colors ${
                    useLlm ? 'border-cyan-400/50 bg-cyan-500/10 text-cyan-100' : 'border-white/10 bg-white/[0.02] text-white/60 hover:bg-white/5'
                  }`}
                >
                  LLM (GPT-5.2)
                </button>
                <button
                  type="button"
                  data-testid="tracking-os-generate-mode-fallback"
                  onClick={() => setUseLlm(false)}
                  className={`flex-1 h-9 text-xs rounded border transition-colors ${
                    !useLlm ? 'border-amber-400/50 bg-amber-500/10 text-amber-100' : 'border-white/10 bg-white/[0.02] text-white/60 hover:bg-white/5'
                  }`}
                >
                  Structured (no LLM)
                </button>
              </div>
            </div>
            <div className="flex items-end">
              <Button
                data-testid="tracking-os-generate-submit"
                onClick={generate}
                disabled={loading || text.trim().length < 20}
                className="w-full bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30 h-9 text-xs"
              >
                {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Generate Draft
              </Button>
            </div>
          </div>

          {/* Advanced: JSON paste */}
          <div className="pt-2 border-t border-white/5">
            <button
              type="button"
              data-testid="tracking-os-generate-advanced-toggle"
              onClick={() => setShowAdvanced((v) => !v)}
              className="text-[11px] text-white/40 hover:text-white/70 inline-flex items-center gap-1"
            >
              <FileJson className="w-3 h-3" />
              {showAdvanced ? 'Hide' : 'Show'} advanced (paste JSON directly)
            </button>
            {showAdvanced && (
              <div className="mt-2 space-y-2">
                <Textarea
                  data-testid="tracking-os-generate-json-textarea"
                  placeholder='{"name":"...", "form_type":"one_time", "questions":[...]}'
                  rows={6}
                  value={jsonPaste}
                  onChange={(e) => setJsonPaste(e.target.value)}
                  className="font-mono text-[11px] bg-slate-950/50 border-white/10"
                />
                <Button
                  data-testid="tracking-os-generate-json-submit"
                  onClick={generateFromJson}
                  disabled={loading || !jsonPaste.trim()}
                  size="sm"
                  variant="outline"
                  className="border-white/10 text-xs h-8"
                >
                  Parse JSON
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {generated && (
        <GeneratedPreviewDialog
          generated={generated}
          onClose={() => setGenerated(null)}
          onSaved={(saved, opts) => { setGenerated(null); reset(); setOpen(false); onSaved?.(saved, opts); }}
          onRegenerate={() => { setGenerated(null); }}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Preview / Edit / Save modal
// ─────────────────────────────────────────────────────────────────
function GeneratedPreviewDialog({ generated, onClose, onSaved, onRegenerate }) {
  const [draft, setDraft] = useState(generated.draft);
  const [saving, setSaving] = useState(false);

  const updateField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updateQuestion = (idx, patch) => setDraft((d) => ({
    ...d,
    questions: d.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)),
  }));
  const removeQuestion = (idx) => setDraft((d) => ({
    ...d, questions: d.questions.filter((_, i) => i !== idx),
  }));

  // Group questions by section_title for visual grouping (display only)
  const groupedSections = (() => {
    const groups = [];
    let cur = null;
    draft.questions.forEach((q, idx) => {
      const title = q.section_title || '(No section)';
      if (!cur || cur.title !== title) {
        cur = { title, items: [] };
        groups.push(cur);
      }
      cur.items.push({ q, idx });
    });
    return groups;
  })();

  const save = async (assignAfter = false) => {
    setSaving(true);
    try {
      // Use the EXISTING template create endpoint — no new save endpoint needed.
      const r = await axios.post(`${API}/tracking/templates`, draft);
      toast.success(`Template saved as draft (${r.data.questions?.length || 0} questions)`);
      onSaved?.(r.data, { assignAfter });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="tracking-os-generate-preview-modal"
        className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col bg-slate-900 border-white/10 text-white"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-cyan-300" />
            Review Generated Template
            <ParserBadge parser={generated.parser_used} />
            <span className="text-[10px] text-white/40 ml-auto">
              {generated.token_usage?.total > 0 && (
                <>tokens: {generated.token_usage.total} · cost ≈ ₹{generated.cost_estimate_inr}</>
              )}
            </span>
          </DialogTitle>
          <DialogDescription className="text-[11px] text-white/50">
            Edit fields, questions, and sections. Saving creates a draft — nothing is published or assigned automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Warnings */}
          {generated.warnings?.length > 0 && (
            <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3">
              <div className="flex items-center gap-2 text-amber-200 text-xs font-medium mb-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                Parser warnings
              </div>
              <ul className="text-[11px] text-amber-100/80 space-y-1 list-disc pl-5">
                {generated.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* Top-level fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Name" testId="gen-field-name" value={draft.name} onChange={(v) => updateField('name', v)} />
            <Field label="Subscriber title" testId="gen-field-sub-title" value={draft.subscriber_title} onChange={(v) => updateField('subscriber_title', v)} />
            <div>
              <Label className="text-[11px] text-white/60 mb-1 block">Form type</Label>
              <Select value={draft.form_type} onValueChange={(v) => updateField('form_type', v)}>
                <SelectTrigger data-testid="gen-field-form-type" className="h-9 text-xs bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">one_time</SelectItem>
                  <SelectItem value="event">event</SelectItem>
                  <SelectItem value="daily">daily</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Category" testId="gen-field-category" value={draft.category || ''} onChange={(v) => updateField('category', v)} />
            <div className="md:col-span-2">
              <Label className="text-[11px] text-white/60 mb-1 block">Description (internal)</Label>
              <Textarea
                data-testid="gen-field-description"
                rows={2}
                value={draft.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                className="bg-white/5 border-white/10 text-xs"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] text-white/60 mb-1 block">Internal tags (comma-separated)</Label>
              <Input
                data-testid="gen-field-tags"
                value={(draft.condition_tags || []).join(', ')}
                onChange={(e) => updateField('condition_tags', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                className="h-9 text-xs bg-white/5 border-white/10"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] text-white/60 mb-1 block">Subscriber reflection</Label>
              <Textarea
                data-testid="gen-field-reflection"
                rows={2}
                value={draft.subscriber_feedback_rules?.default || ''}
                onChange={(e) => updateField('subscriber_feedback_rules', { ...(draft.subscriber_feedback_rules || {}), default: e.target.value })}
                className="bg-white/5 border-white/10 text-xs"
              />
            </div>
          </div>

          {/* Questions grouped by section */}
          <div className="space-y-3 pt-3 border-t border-white/5">
            <div className="flex items-center gap-2 text-xs text-white/70">
              <Info className="w-3 h-3" />
              {draft.questions.length} questions across {groupedSections.length} section{groupedSections.length === 1 ? '' : 's'}
            </div>
            {groupedSections.map((group, gi) => (
              <div key={gi} className="rounded border border-white/5 bg-white/[0.02]">
                <div className="px-3 py-2 border-b border-white/5 text-[11px] uppercase tracking-wider text-cyan-200/80">
                  {group.title}
                </div>
                <div className="p-3 space-y-3">
                  {group.items.map(({ q, idx }) => (
                    <QuestionEditor
                      key={q.key}
                      q={q}
                      idx={idx}
                      onChange={(patch) => updateQuestion(idx, patch)}
                      onRemove={() => removeQuestion(idx)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="border-t border-white/5 pt-3">
          <Button
            data-testid="tracking-os-generate-discard"
            variant="ghost"
            onClick={onClose}
            className="text-white/60 hover:text-white"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Discard
          </Button>
          <Button
            data-testid="tracking-os-generate-regenerate"
            variant="outline"
            onClick={onRegenerate}
            className="border-white/10 text-white/80"
          >
            Re-generate
          </Button>
          <Button
            data-testid="tracking-os-generate-save-draft"
            onClick={() => save(false)}
            disabled={saving || !draft.name || !draft.questions?.length}
            className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Save as Draft
          </Button>
          <Button
            data-testid="tracking-os-generate-save-and-assign"
            onClick={() => save(true)}
            disabled={saving || !draft.name || !draft.questions?.length}
            className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 hover:bg-emerald-500/30"
          >
            Save &amp; Assign →
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ParserBadge({ parser }) {
  const map = {
    llm: { label: 'LLM (GPT-5.2)', cls: 'bg-cyan-500/20 text-cyan-100 border-cyan-400/40' },
    fallback: { label: 'Structured fallback', cls: 'bg-amber-500/20 text-amber-100 border-amber-400/40' },
    json_paste: { label: 'JSON paste', cls: 'bg-violet-500/20 text-violet-100 border-violet-400/40' },
  };
  const m = map[parser] || { label: parser, cls: 'bg-white/10 text-white/70 border-white/10' };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${m.cls}`}>{m.label}</span>;
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] text-white/60 mb-1 block">{label}</Label>
      <Input
        data-testid={testId}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 text-xs bg-white/5 border-white/10"
      />
    </div>
  );
}

const TYPE_OPTIONS = [
  'single_choice', 'multi_choice', 'textarea', 'text', 'yes_no',
  'scale_1_10', 'slider', 'number', 'date', 'time',
];

function QuestionEditor({ q, idx, onChange, onRemove }) {
  const optionsEditable = q.type === 'single_choice' || q.type === 'multi_choice';

  const updateOption = (oi, val) => {
    const opts = [...(q.options || [])];
    opts[oi] = val;
    onChange({ options: opts });
  };
  const addOption = () => onChange({ options: [...(q.options || []), 'New option'] });
  const removeOption = (oi) => onChange({ options: (q.options || []).filter((_, i) => i !== oi) });

  return (
    <div
      data-testid={`gen-question-${idx}`}
      className="rounded border border-white/5 bg-slate-950/30 p-2"
    >
      <div className="flex items-start gap-2">
        <span className="text-[10px] text-white/30 font-mono pt-1.5 shrink-0">Q{idx + 1}</span>
        <Textarea
          data-testid={`gen-question-${idx}-label`}
          rows={1}
          value={q.label}
          onChange={(e) => onChange({ label: e.target.value })}
          className="bg-white/5 border-white/10 text-xs flex-1"
        />
        <Button
          data-testid={`gen-question-${idx}-remove`}
          size="sm"
          variant="ghost"
          onClick={onRemove}
          className="text-rose-300 hover:text-rose-200 h-7 px-2"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-2">
        <div>
          <Label className="text-[10px] text-white/40">Type</Label>
          <Select value={q.type} onValueChange={(v) => onChange({ type: v })}>
            <SelectTrigger data-testid={`gen-question-${idx}-type`} className="h-8 text-[11px] bg-white/5 border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-white/40">Mapping key</Label>
          <Input
            data-testid={`gen-question-${idx}-mapping-key`}
            value={q.mapping_key || ''}
            onChange={(e) => onChange({ mapping_key: e.target.value })}
            className="h-8 text-[11px] bg-white/5 border-white/10"
          />
        </div>
        <div>
          <Label className="text-[10px] text-white/40">Section</Label>
          <Input
            data-testid={`gen-question-${idx}-section`}
            value={q.section_title || ''}
            onChange={(e) => onChange({ section_title: e.target.value })}
            className="h-8 text-[11px] bg-white/5 border-white/10"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-[11px] text-white/70 cursor-pointer">
            <input
              type="checkbox"
              data-testid={`gen-question-${idx}-required`}
              checked={!!q.required}
              onChange={(e) => onChange({ required: e.target.checked })}
            />
            Required
          </label>
        </div>
      </div>

      {optionsEditable && (
        <div className="mt-2 space-y-1">
          <Label className="text-[10px] text-white/40">Options</Label>
          {(q.options || []).map((opt, oi) => (
            <div key={oi} className="flex items-center gap-1">
              <Input
                data-testid={`gen-question-${idx}-option-${oi}`}
                value={opt}
                onChange={(e) => updateOption(oi, e.target.value)}
                className="h-7 text-[11px] bg-white/5 border-white/10"
              />
              <Button
                size="sm" variant="ghost"
                onClick={() => removeOption(oi)}
                className="h-7 w-7 p-0 text-rose-300/70 hover:text-rose-300"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={addOption}
            className="h-7 text-[11px] border-white/10 text-white/60"
          >
            <Plus className="w-3 h-3 mr-1" /> Add option
          </Button>
        </div>
      )}
    </div>
  );
}
